import React, { useEffect, useState } from 'react';
import { useTapChangeLog } from '../hooks/useTapChangeLog';
import { TapHistoryTable } from '../components/TapHistoryTable';
import { LocationPills } from '../components/LocationPills';
import { useLocation } from '../lib/location';
import type { TapChangeLogEntry } from '../api/types';

const PAGE_SIZE = 50;

export function TapHistoryPage(): React.ReactElement {
  const { locations } = useLocation();

  const [filterLocationId, setFilterLocationId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<TapChangeLogEntry[]>([]);

  const { data, isFetching, isPlaceholderData } = useTapChangeLog({
    ...(filterLocationId !== null ? { locationId: filterLocationId } : {}),
    offset,
    limit: PAGE_SIZE,
  });

  useEffect(() => {
    if (!data || isPlaceholderData) return;
    if (data.offset === 0) {
      setAllItems(data.items);
    } else {
      setAllItems(prev => [...prev, ...data.items]);
    }
  }, [data, isPlaceholderData]);

  function handleLocationChange(id: string | null) {
    setFilterLocationId(id);
    setOffset(0);
    setAllItems([]);
  }

  const isAllActive = filterLocationId === null;

  const allPillStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 12px',
    borderRadius: 4,
    fontSize: '0.8125rem',
    fontWeight: 700,
    background: active ? 'var(--bg)' : 'transparent',
    color: active ? 'var(--text)' : 'var(--text-muted)',
    border: active ? '1px solid var(--border)' : '1px solid transparent',
    cursor: 'pointer',
  });

  return (
    <div style={{ padding: '24px 32px 64px', maxWidth: 1480, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em' }}>
          Tap History
        </h1>
        <span style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: '0.9375rem', display: 'block' }}>
          {data?.total ?? 0} events
        </span>
      </div>

      {/* Location filter row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 20,
        flexWrap: 'wrap',
      }}>
        {/* "All" standalone pill */}
        <div style={{
          display: 'inline-flex',
          background: 'var(--bg-subtle)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: 2,
        }}>
          <button
            onClick={() => handleLocationChange(null)}
            aria-pressed={isAllActive}
            style={allPillStyle(isAllActive)}
          >
            All
          </button>
        </div>

        {/* Per-location pills */}
        <LocationPills
          locations={locations}
          selectedId={filterLocationId}
          onChange={(id) => handleLocationChange(id)}
        />
      </div>

      {/* Table */}
      <TapHistoryTable
        items={allItems}
        showLocationColumn={filterLocationId === null}
        isLoading={isFetching && offset === 0}
      />

      {/* Load more */}
      {allItems.length < (data?.total ?? 0) && (
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
          <button
            disabled={isFetching}
            onClick={() => setOffset(prev => prev + PAGE_SIZE)}
            style={{
              padding: '8px 20px',
              borderRadius: 6,
              fontSize: '0.875rem',
              fontWeight: 700,
              background: 'var(--bg-subtle)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              cursor: isFetching ? 'not-allowed' : 'pointer',
              opacity: isFetching ? 0.6 : 1,
            }}
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );
}
